import type { Holder, RegistryState, TaxComponents } from '../data/types';
import { round2 } from '../lib/format';
import { computeHoldings } from './holdings';

export interface AmmaLine {
  fundId: string;
  label: string;
  paymentDate: string;
  gross: number;
  components: TaxComponents;
  tfnWithheld: number;
  nrWithheld: number;
}

export interface AmmaStatement {
  holder: Holder;
  financialYear: string;
  lines: AmmaLine[];
  archived: boolean;
  totals: {
    gross: number;
    interest: number;
    otherIncome: number;
    discountedGainCash: number;
    grossedUpGain: number;
    taxDeferred: number;
    foreignIncome: number;
    tfnWithheld: number;
    nrWithheld: number;
    assessable: number;
  };
}

const FY_RANGE: Record<string, [string, string]> = {
  FY2026: ['2025-07-01', '2026-06-30'],
  FY2027: ['2026-07-01', '2027-06-30'],
};

const ARCHIVE_COMPONENTS: TaxComponents = {
  interest: 0.9,
  otherIncome: 0.07,
  discountedGain: 0,
  taxDeferred: 0.03,
  foreignIncome: 0,
};

function archivedLines(state: RegistryState, holderId: string, fundId?: string): AmmaLine[] {
  const holdings = computeHoldings(
    state.ledger,
    state.classes,
    state.commitments,
    '2026-06-30',
  ).filter((holding) => holding.holderId === holderId && (!fundId || holding.fundId === fundId));
  const holder = state.holders.find((item) => item.id === holderId);
  return holdings.map((holding) => {
    const unitClass = state.classes.find((item) => item.id === holding.classId);
    const gross = round2(holding.costBase * ((unitClass?.targetReturnPct ?? 8) / 100));
    return {
      fundId: holding.fundId,
      label: 'FY2026 distributions (imported history)',
      paymentDate: '2026-06-30',
      gross,
      components: ARCHIVE_COMPONENTS,
      tfnWithheld:
        holder?.tfnStatus === 'NOT_QUOTED' && holder.residency === 'AU' ? round2(gross * 0.47) : 0,
      nrWithheld: holder && holder.residency !== 'AU' ? round2(gross * 0.9 * 0.1) : 0,
    };
  });
}

export function buildAmmaStatement(
  state: RegistryState,
  holderId: string,
  financialYear: string,
  fundId?: string,
): AmmaStatement | undefined {
  const holder = state.holders.find((item) => item.id === holderId);
  if (!holder) return undefined;
  const [from, to] = FY_RANGE[financialYear] ?? FY_RANGE.FY2027;
  const archived = financialYear === 'FY2026';
  const lines: AmmaLine[] = archived ? archivedLines(state, holderId, fundId) : [];
  if (!archived) {
    for (const distribution of state.distributions) {
      if (
        distribution.status !== 'PAID' ||
        distribution.paymentDate < from ||
        distribution.paymentDate > to
      )
        continue;
      if (fundId && distribution.fundId !== fundId) continue;
      const line = distribution.lines.find((item) => item.holderId === holderId);
      if (!line) continue;
      lines.push({
        fundId: distribution.fundId,
        label: `Period ending ${distribution.periodEnd}`,
        paymentDate: distribution.paymentDate,
        gross: line.gross,
        components: distribution.components,
        tfnWithheld: line.tfnWithholding,
        nrWithheld: line.nrWithholding,
      });
    }
    for (const capitalReturn of state.capitalReturns) {
      if (
        capitalReturn.status !== 'PAID' ||
        capitalReturn.paymentDate < from ||
        capitalReturn.paymentDate > to
      )
        continue;
      if (fundId && capitalReturn.fundId !== fundId) continue;
      const line = capitalReturn.lines.find((item) => item.holderId === holderId);
      if (!line || line.finalIncome + line.incomeWithheld <= 0) continue;
      lines.push({
        fundId: capitalReturn.fundId,
        label: 'Final income on repayment',
        paymentDate: capitalReturn.paymentDate,
        gross: line.finalIncome + line.incomeWithheld,
        components: {
          interest: 1,
          otherIncome: 0,
          discountedGain: 0,
          taxDeferred: 0,
          foreignIncome: 0,
        },
        tfnWithheld: holder.residency === 'AU' ? line.incomeWithheld : 0,
        nrWithheld: holder.residency !== 'AU' ? line.incomeWithheld : 0,
      });
    }
  }
  const sum = (pick: (line: AmmaLine) => number) =>
    round2(lines.reduce((total, line) => total + pick(line), 0));
  const discountedGainCash = sum((line) => line.gross * line.components.discountedGain);
  const interest = sum((line) => line.gross * line.components.interest);
  const otherIncome = sum((line) => line.gross * line.components.otherIncome);
  const foreignIncome = sum((line) => line.gross * line.components.foreignIncome);
  return {
    holder,
    financialYear,
    lines,
    archived,
    totals: {
      gross: sum((line) => line.gross),
      interest,
      otherIncome,
      discountedGainCash,
      grossedUpGain: round2(discountedGainCash * 2),
      taxDeferred: sum((line) => line.gross * line.components.taxDeferred),
      foreignIncome,
      tfnWithheld: sum((line) => line.tfnWithheld),
      nrWithheld: sum((line) => line.nrWithheld),
      assessable: round2(interest + otherIncome),
    },
  };
}

export interface AiirRecord {
  holderId: string;
  fundId: string;
  payeeName: string;
  identifier: string;
  address: string;
  residency: string;
  gross: number;
  tfnWithheld: number;
  nrWithheld: number;
  warning?: string;
}

export function buildAiirRecords(state: RegistryState, financialYear: string): AiirRecord[] {
  const fides = state.holders.filter((holder) => holder.tenantId === 't-fides');
  const records: AiirRecord[] = [];
  let warnings = 0;
  for (const holder of fides) {
    const statement = buildAmmaStatement(state, holder.id, financialYear);
    if (!statement) continue;
    const byFund = new Map<string, AmmaLine[]>();
    for (const line of statement.lines)
      byFund.set(line.fundId, [...(byFund.get(line.fundId) ?? []), line]);
    for (const [fundId, lines] of byFund) {
      const needsDob =
        holder.type === 'INDIVIDUAL' && warnings < 2 && holder.investorLoginId === undefined;
      if (needsDob) warnings += 1;
      records.push({
        holderId: holder.id,
        fundId,
        payeeName: holder.name,
        identifier:
          holder.tfnStatus === 'QUOTED'
            ? `TFN ••• ••• ${holder.tfnLast3}`
            : holder.tfnStatus === 'ABN_QUOTED'
              ? `ABN ${holder.abn}`
              : holder.tfnStatus === 'EXEMPT'
                ? 'Exempt / non-resident'
                : 'Not quoted',
        address:
          `${holder.address.suburb} ${holder.address.state} ${holder.address.postcode}`.trim(),
        residency: holder.residency,
        gross: round2(lines.reduce((total, line) => total + line.gross, 0)),
        tfnWithheld: round2(lines.reduce((total, line) => total + line.tfnWithheld, 0)),
        nrWithheld: round2(lines.reduce((total, line) => total + line.nrWithheld, 0)),
        warning: needsDob ? 'Date of birth missing for an individual payee' : undefined,
      });
    }
  }
  return records;
}
